import React, { useState, useEffect } from 'react';
import '../App.css';
import { useToastContext } from './Toast';
import { bech32 } from '@scure/base';
import { hexToBytes } from '@noble/hashes/utils';
import DefaultAvatar from '../Images/default avatar.svg';
import RemoveButton from '../Images/RemoveButton.svg';
import NotesButton from '../Images/NotesButton.svg';
import SubmitButton from '../Images/SubmitBtn.svg';
import CancelBtn from '../Images/Cancel2Btn.svg';

const Bech32MaxSize = 5000;

const npubEncode = (hex) => {
    try {
        const words = bech32.toWords(hexToBytes(hex));
        return bech32.encode('npub', words, Bech32MaxSize);
    } catch (error) {
        console.error("Error encoding to npub:", error);
        return "Invalid Public Key";
    }
};

const formatDate = (timestamp) => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day:   '2-digit',
    year:  'numeric',
  });
};


const ExpiredSubs = () => {
    const [expiredSubs, setExpiredSubs] = useState([]);
    const [avatars, setAvatars] = useState({});
    const [loading, setLoading] = useState(false);
    const { sendSuccess, sendWarning } = useToastContext();
    const [notesPopup, setNotesPopup] = useState(null);
    const [notesText, setNotesText] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');


    const fetchAvatars = async (pubKeys) => {
        const payload = JSON.stringify(["user_infos", { "pubkeys": pubKeys }]);
        try {
            const response = await fetch("https://dev.primal.net/api", {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: payload,
            });

            if (response.ok) {
                const data = await response.json();
                const avatarsData = {};
                if (data && Array.isArray(data)) {
                    data.forEach(user => {
                        try {
                            const userData = JSON.parse(user.content);
                            avatarsData[user.pubkey] = userData.picture || DefaultAvatar;
                        } catch (err) {
                            console.error("Error parsing user content:", err);
                        }
                    });
                }
                setAvatars(avatarsData);
            } else {
                console.error("Error fetching avatars:", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching avatars:", error);
        }
    };

   const fetchExpiredSubscriptions = async (filter = 'all') => {
  setLoading(true);
  try {
    const e = await window.nostr.signEvent({
      created_at: Math.floor(new Date().getTime() / 1000),
      kind: 10000222,
      tags: [],
      content: 'i want in',
    });

    const args =
      filter === 'all'
        ? {}
        : { expiration_review_status: filter };

    const response = await fetch('https://admin.primal.net/api2', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_from_user: e,
        request: ['expired_subscriptions', args],
      }),
    });

    const data = await response.json();
    if (data && !data.error) {
      setExpiredSubs(data);
      const pubKeys = [...new Set(data.map((sub) => sub.pubkey))];
      fetchAvatars(pubKeys);
    } else {
      sendWarning('Failed to fetch expired subscriptions. Please try again.');
    }
  } catch (error) {
    console.error('Error fetching expired subscriptions:', error);
    sendWarning('Failed to fetch expired subscriptions. Please try again.');
  } finally {
    setLoading(false);
  }
};

    const handleRemoveMembership = async (sub) => {
        const name = sub.name || 'Unknown';
        if (!window.confirm(`Are you sure you want to remove ${name}'s membership?`)) return;
        try {
            const e = await window.nostr.signEvent({
                created_at: Math.floor(Date.now() / 1000),
                kind: 10000222,
                tags: [],
                content: 'membership_remove',
            });
            const response = await fetch('https://admin.primal.net/api2', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    event_from_user: e,
                    request: ['membership_remove', { pubkey: sub.pubkey, dry_run: false }],
                }),
            });
            if (response.ok) {
                sendSuccess(`Membership removed for ${name}`);
                setExpiredSubs(prev => prev.filter(s => s.pubkey !== sub.pubkey));
            } else {
                sendWarning(`Failed to remove membership for ${name}`);
            }
        } catch (error) {
            console.error('Error removing membership:', error);
            sendWarning('Error removing membership. Please try again.');
        }
    };

    const handleOpenNotes = (sub) => {
        setNotesPopup(sub);
        setNotesText(sub.expiration_review_notes || '');
    };


    // --- Status helpers/state ---
const [selectedStatus, setSelectedStatus] = useState('not_started');

const normalizeApiStatus = (api) => {
  switch (api) {
    case 'in_progress': return 'in_progress';
    case 'handled': return 'handled';
    case 'not_started':
    default: return 'not_started';
  }
};

useEffect(() => {
  if (notesPopup) {
    setSelectedStatus(normalizeApiStatus(notesPopup.expiration_review_status));
  }
}, [notesPopup]);

const handleSubmitNotes = async () => {
  if (!notesPopup) return;

  try {
    // 1) Update notes (existing)
    {
      const e = await window.nostr.signEvent({
        created_at: Math.floor(Date.now() / 1000),
        kind: 10000222,
        tags: [],
        content: 'update_membership_expiration_review',
      });

      const resp = await fetch('https://admin.primal.net/api2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_from_user: e,
          request: ['update_membership_expiration_review', { pubkey: notesPopup.pubkey, notes: notesText }],
        }),
      });

      if (!resp.ok) {
        sendWarning('Failed to update notes.');
        return;
      }
    }

    const original = normalizeApiStatus(notesPopup.expiration_review_status);
    if (selectedStatus !== original) {
      const e2 = await window.nostr.signEvent({
        created_at: Math.floor(Date.now() / 1000),
        kind: 10000222,
        tags: [],
        content: 'update_membership_expiration_review_status',
      });

      const resp2 = await fetch('https://admin.primal.net/api2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_from_user: e2,
          request: [
            'update_membership_expiration_review_status',
            { pubkey: notesPopup.pubkey, status: selectedStatus }
          ],
        }),
      });

      if (!resp2.ok) {
        sendWarning('Failed to update review status.');
        return;
      }
    }

    sendSuccess('Notes & status updated.');
    setNotesPopup(null);
    fetchExpiredSubscriptions(selectedFilter);
  } catch (error) {
    console.error('Error submitting notes/status:', error);
    sendWarning('Error submitting changes.');
  }
};



const formatProductId = (pid) => {
  if (!pid) return 'N/A';
  const lower = String(pid).toLowerCase();

  if (/\bpremium\b/.test(lower)) return 'premium';
  if (/\bpro\b/.test(lower)) return 'pro';

  return pid;
};

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    if (sendSuccess) sendSuccess('Copied');
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text ?? '';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      if (sendSuccess) sendSuccess('Copied');
    } catch {
      if (sendWarning) sendWarning('Unable to copy');
    } finally {
      document.body.removeChild(ta);
    }
  }
};

const formatReviewStatus = (status) => {
  if (!status) return 'N/A';
  const s = String(status).toLowerCase();
  switch (s) {
    case 'not_started': return 'not started';
    case 'in_progress': return 'in progress';
    case 'handled':     return 'handled';
    default:            return status; // fallback if API adds new values
  }
};


useEffect(() => {
  fetchExpiredSubscriptions(selectedFilter);
}, [selectedFilter]);


    return (
        <div className="MainBody">
             <div className="UMHeaderRow">
                <div className="UMTitle">expired subs</div>

                <select
                    className="UMFilterSelect"
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                >
                    <option value="all">All</option>
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progess</option>
                    <option value="handled">Handled</option>
                </select>
                </div>
             <div className="LHeaders">
                <div className="LHeader1">User</div>
                <div className="LHeader2">Valid Until</div>
                <div className="LHeader3">Type</div>
                <div className="LHeader4">Active</div>
                <div className="LHeader5">Origin</div>
                <div className="LHeader6">Reason</div>
                <div className="LHeader7">Status</div>
             </div>
            <div className="SubscriptionList">
            {loading ? (
                <p>Loading...</p>
            ) : (
                expiredSubs.map((sub, index) => (
                <div key={index} className="SubscriptionRow">
                    <div className="Avatar">
                    <a href={`https://primal.net/p/${npubEncode(sub.pubkey)}`} target="_blank" rel="noopener noreferrer">
                        <img src={avatars[sub.pubkey] || DefaultAvatar} alt="Avatar" className="avatar-img" />
                    </a>
                    </div>
                    <div className="SubscriptionCell SubscriptionName">
                    <a href={`https://primal.net/p/${npubEncode(sub.pubkey)}`} target="_blank" rel="noopener noreferrer" className="NameLink">
                        {sub.name || 'Unknown'}
                    </a>
                    </div>
                    <div className="SubscriptionCell SubscriptionValidUntil">{formatDate(sub.valid_until)}</div>
                    <div className="SubscriptionCell SubscriptionType">{formatProductId(sub.product_id)}</div>
                    <div className="SubscriptionCell SubscriptionStatus">{sub.active ? 'true' : 'false'}</div>
                    <div className="SubscriptionCell SubscriptionOrigin">{sub.origin || 'N/A'}</div>
                    <div className="SubscriptionCell SubscriptionReason">{sub.reason || 'N/A'}</div>
                    <div className="SubscriptionCell SubscriptionSupportStatus">
                    {formatReviewStatus(sub.expiration_review_status)}
                    </div>
                    <div className="SubscriptionCell SubscriptionNotes">
                    <button className="ExpiredNotesButton" onClick={() => handleOpenNotes(sub)}>
                        <img src={NotesButton} alt="Notes" className="ExpiredNotesButton" />
                    </button>
                    </div>
                    <div className="SubscriptionCell SubscriptionRemove">
                    <button className="RemoveExpiredButton" onClick={() => handleRemoveMembership(sub)}>
                        <img src={RemoveButton} alt="Remove" className="RemoveExpiredButton" />
                    </button>
                    </div>
                </div>
                ))
            )}
            </div>

            {notesPopup && (
                <div className="NotesModalOverlay">
                    <div className="NotesModal">
                        <div className="ExpiredUserInfo">
                            {/* Row 1 */}
                           <div className="InfoItem">
                            <div className="InfoLabel">First Name</div>
                            <div className="InfoValue">
                            <span className="InfoValueText">{notesPopup.first_name || 'N/A'}</span>
                            {notesPopup.first_name && (
                                <button className="CopyBtn" onClick={() => copy(notesPopup.first_name)}>copy</button>
                            )}
                            </div>
                        </div>

                        <div className="InfoItem">
                            <div className="InfoLabel">Last Name</div>
                            <div className="InfoValue">
                            <span className="InfoValueText">{notesPopup.last_name || 'N/A'}</span>
                            {notesPopup.last_name && (
                                <button className="CopyBtn" onClick={() => copy(notesPopup.last_name)}>copy</button>
                            )}
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="InfoItem">
                            <div className="InfoLabel">Email</div>
                            <div className="InfoValue">
                            <span className="InfoValueText">{notesPopup.email || 'N/A'}</span>
                            {notesPopup.email && (
                                <button className="CopyBtn" onClick={() => copy(notesPopup.email)}>copy</button>
                            )}
                            </div>
                        </div>
                            <div className="InfoItem">
                            <div className="InfoLabel">Product Type</div>
                            <div className="InfoValue">{formatProductId(notesPopup.product_id)}</div>
                            </div>

                            {/* Row 3 (two columns) */}
                            <div className="InfoItem">
                            <div className="InfoLabel">Created At</div>
                            <div className="InfoValue">
                                <span className="InfoValueText">{formatDate(notesPopup.created_at)}</span>
                            </div>
                            </div>

                            <div className="InfoItem">
                            <div className="InfoLabel">Review Status</div>
                            <div className="InfoValue">
                                <select
                                className="StatusSelect"
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                <option value="not_started">not started</option>
                                <option value="in_progress">in progess</option>{/* UI label kept as requested */}
                                <option value="handled">handled</option>
                                </select>
                            </div>
                            </div>
                        </div>
                        <textarea
                            value={notesText}
                            onChange={(e) => setNotesText(e.target.value)}
                            placeholder="Enter expiration review notes..."
                            className="NotesTextarea"
                        />
                        <div className="NotesModalActions">
                            <button onClick={handleSubmitNotes} className="NotesSubmitBtn">
                                <img src={SubmitButton} alt="Submit"></img>
                            </button>
                            <button onClick={() => setNotesPopup(null)} className="NotesCancelButton">
                                <img src={CancelBtn} alt="Cancel"></img>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpiredSubs;
